'use client'

import { useContext } from 'react'
import { ThemeContext } from '@/src/contexts'
import { AmplitudeContext } from '@/src/contexts/Amplitude/AmplitudeContext'
import { trackBookingEvents } from '@/src/lib'

import { Card, Typography } from 'antd'
import { Button, PhoneButton, ShortDisclaimerAlert } from '@/src/components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Physician } from '@/src/interfaces'

const { Text, Title } = Typography

type BookingOption = {
  type: 'online' | 'phone'
  value: string
}

interface BookingOptionsCardProps {
  bookingOption: BookingOption
  helpText?: boolean
  physician: Physician
  isRequestAppointmentModal?: boolean
  position: string
}

export const BookingOptionsCard = ({
  bookingOption,
  helpText,
  physician,
  isRequestAppointmentModal,
  position,
}: BookingOptionsCardProps) => {
  const { isDarkMode } = useContext(ThemeContext)
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)
  const isOnlineBooking = bookingOption.type === 'online'

  const handleOnlineBooking = () => {
    // Amplitude: Track the online booking click
    trackBookingEvents.onlineBookingClicked(trackAmplitudeEvent)({
      npi: physician.npi,
      full_name: physician.full_name,
      specialty: physician.specialty,
      city: physician.city,
      state: physician.state,
      booking_url: physician.booking_url,
      telehealth_appointment: physician.telehealth_appointment,
      position,
    })
  }

  return (
    <Card
      style={{
        width: '100%',
        borderColor: isOnlineBooking ? 'gray' : '#595959',
      }}
    >
      <div className='flex flex-col w-full justify-center items-center text-center'>
        <FontAwesomeIcon
          icon={isOnlineBooking ? 'calendar-check' : 'phone'}
          style={{ color: `${isDarkMode ? '#38A7C8' : '#274765'}` }}
          className='text-5xl mb-6'
        />
        <Title level={5} style={{ fontWeight: 300 }}>
          {isOnlineBooking
            ? 'Book an appointment directly online.'
            : 'Call to book your appointment.'}
        </Title>
        {isOnlineBooking && bookingOption.type === 'online' && (
          <Button
            type='primary'
            className='mt-8'
            href={bookingOption.value}
            rel='noopener'
            target='_blank'
            onClick={handleOnlineBooking}
            text='Book Online'
          />
        )}
        {physician.phone && bookingOption.type === 'phone' && (
          <PhoneButton
            physician={physician}
            text='View Phone Number'
            color='primary'
            variant='solid'
            className='mt-8'
          />
        )}
      </div>
      {helpText && physician.phone && (
        <div className='flex flex-col items-center justify-center my-12'>
          <Text>Need help scheduling?</Text>
          <PhoneButton
            physician={physician}
            text='View Phone Number'
            color='primary'
            variant='solid'
            className='mt-8'
          />
        </div>
      )}
      {!isRequestAppointmentModal && <ShortDisclaimerAlert />}
    </Card>
  )
}
